import { format } from "date-fns";
import { GetReportDataResponse } from "src/services/restApi/queries/report.service";
import {
  AggregatedTradeData,
  Grouping,
  RawData,
  Trade,
  TransactionStatusEnum,
} from "../types";
import { TRADES_GROUP_BY_OPTIONS } from "./consts";
import {
  capitaliseFirstLetter,
  centsToDollars,
  formatTradeTime,
} from "./utils";

export function getTradeGroupOptions(trades: RawData<Trade>, group: Grouping) {
  const uniqueValues = new Set<string>();
  trades.forEach((trade) => {
    const date = new Date(trade.createdAt);
    let riskFlag: string;
    switch (group.groupBy) {
      case "instrument":
        uniqueValues.add(trade.asset || "Unknown Instrument");
        break;
      case "timeframe":
        uniqueValues.add(
          trade.tradeTime
            ? formatTradeTime(Number(trade.tradeTime))
            : "Unknown Timeframe"
        );
        break;
      case "country":
        uniqueValues.add(trade.country || "Unknown Country");
        break;
      case "client":
        uniqueValues.add(trade.userId || "Unknown Client");
        break;
      case "accountGroup":
        riskFlag = trade.riskFlag || "0";
        uniqueValues.add(riskFlag === "0" ? "Normal" : riskFlag.toLowerCase());
        break;
      case "time":
        if (group.timeGroup === "month") {
          uniqueValues.add(format(date, "MMMM yyyy"));
        } else if (group.timeGroup === "week") {
          uniqueValues.add(format(date, "yyyy-'W'ww"));
        } else if (group.timeGroup === "day") {
          uniqueValues.add(format(date, "yyyy-MM-dd"));
        }
        break;
    }
  });
  return Array.from(uniqueValues).sort();
}

export function aggregateTradeData(
  groupings: Grouping[],
  trades: RawData<Trade>
): AggregatedTradeData[] {
  const groupedData = new Map<string, AggregatedTradeData>();

  trades.forEach((trade) => {
    const groupKeyParts = groupings.map((g) => {
      const date = new Date(trade.createdAt);
      let riskFlag: string;
      switch (g.groupBy) {
        case "instrument":
          return trade.asset || "Unknown Instrument";
        case "timeframe":
          return trade.tradeTime
            ? formatTradeTime(Number(trade.tradeTime))
            : "Unknown Timeframe";
        case "country":
          return trade.country || "Unknown Country";
        case "accountGroup":
          riskFlag = trade.riskFlag || "0";
          return riskFlag === "0" ? "Normal" : riskFlag.toLowerCase();
        case "time":
          if (g.timeGroup === "month") {
            return format(date, "MMMM yyyy") || "Unknown Time";
          } else if (g.timeGroup === "week") {
            return format(date, "yyyy-'W'ww") || "Unknown Time";
          } else if (g.timeGroup === "day") {
            return format(date, "yyyy-MM-dd") || "Unknown Time";
          }
          return "Unknown Time";
        case "client":
        default:
          return trade.userId || "Unknown Client";
      }
    });
    const groupKey = groupKeyParts.join(" + ");
    if (!groupedData.has(groupKey)) {
      groupedData.set(groupKey, {
        userId: trade.userId || "",
        tradingResult: 0,
        numberOfTrades: 0,
        tradingTurnover: 0,
        winningTradesCount: 0,
        drawTradesCount: 0,
        losingTradesCount: 0,
        revertedTradesCount: 0,
        winRate: 0,
        lossRate: 0,
        drawRate: 0,
        revertRate: 0,
        groupKey,
        groupValues: groupKeyParts,
        trades: [],
      });
    }
    const group = groupedData.get(groupKey);
    if (!group) return;

    group.trades.push(trade);
    group.numberOfTrades++;
    group.tradingTurnover += centsToDollars(trade.amount);
    if (trade.transactionStatus === TransactionStatusEnum.Won) {
      group.winningTradesCount++;
      group.tradingResult -=
        ((trade.potentialWinnings || 0) - trade.amount) / 100;
    } else if (trade.transactionStatus === TransactionStatusEnum.Lost) {
      group.losingTradesCount++;
      group.tradingResult += trade.amount / 100;
    } else if (trade.transactionStatus === TransactionStatusEnum.Reverted) {
      group.revertedTradesCount++;
    } else if (trade.transactionStatus === TransactionStatusEnum.Draw) {
      group.drawTradesCount++;
    }
    group.winRate = (group.winningTradesCount / group.numberOfTrades) * 100;
    group.lossRate = (group.losingTradesCount / group.numberOfTrades) * 100;
    group.revertRate = (group.revertedTradesCount / group.numberOfTrades) * 100;
    group.drawRate = (group.drawTradesCount / group.numberOfTrades) * 100;
  });
  return Array.from(groupedData.values());
}

export function createTradesCsv(
  groupings: Grouping[],
  data: GetReportDataResponse,
  aggregatedData: AggregatedTradeData[]
): string {
  let csvContent = "";
  if (groupings.length === 0 && data.data) {
    const headers = [
      "Trade ID",
      "Client ID",
      "Trade Status",
      "Trade Direction",
      "Instrument",
      "Bet Amount (USD)",
      "Trading Result (USD)",
      "Risk Flag",
      "Payout Ratio",
      "Created At",
      "Trade Length",
      "User Country",
      "Game ID",
      "Transaction Type",
      "Product",
      "Currency",
      "Potential Winnings (USD)",
      "Updated At",
      "Trade Timestamp",
      "Admin Action",
      "Admin Reason",
      "Admin ID",
    ];

    csvContent = [
      headers.join(","),
      ...(data.data as RawData<Trade>).map((trade) => {
        return [
          `${trade.tradeId.toLocaleString()}`,
          `${trade.userId.toLocaleString()}`,
          `${capitaliseFirstLetter(
            trade.transactionStatus.toLocaleString().toLowerCase()
          )}`,
          `${capitaliseFirstLetter(
            trade.tradeDirection.toLocaleString().toLowerCase()
          )}`,
          `${trade.asset.toLocaleString()}`,
          `${trade.amount / 100}`,
          `${
            trade.transactionStatus === TransactionStatusEnum.Won
              ? `-${Math.abs(
                  ((trade.potentialWinnings || 0) - trade.amount) / 100
                )}`
              : trade.transactionStatus === TransactionStatusEnum.Lost
              ? `${trade.amount / 100}`
              : "0"
          }`,
          `${
            trade.riskFlag === null
              ? ""
              : trade.riskFlag === "0"
              ? "Normal"
              : capitaliseFirstLetter(trade.riskFlag.toLowerCase())
          }`,
          `${
            trade.payoutRatio === null
              ? "-"
              : trade.payoutRatio.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
          }`,
          `${new Date(trade.createdAt).toISOString()}`,
          `${
            trade.tradeTime !== null ? formatTradeTime(trade.tradeTime) : "-"
          }`,
          `${trade.country ? trade.country.toLocaleString() : "-"}`,
          `${trade.gameId.toLocaleString()}`,
          `${trade.transactionType.toLocaleString()}`,
          `${trade.product.toLocaleString()}`,
          `${trade.currency.toLocaleString()}`,
          `${(trade.potentialWinnings || 0) / 100}`,
          `${new Date(trade.updatedAt).toISOString()}`,
          `${
            trade.tradeTimestamp ? Number.parseInt(trade.tradeTimestamp) : "-"
          }`,
          `${trade.adminAction ?? "-"}`,
          `${trade.adminReason ?? "-"}`,
          `${trade.adminId ?? "-"}`,
        ].join(",");
      }),
    ].join("\n");
  } else {
    // Get the filtered data
    const filteredData = aggregatedData.filter((row) => {
      const keyParts = row.groupKey.split(" + ");
      return groupings.every((g, idx) => {
        const filter: string[] = g.groupFilter;
        if (!filter || filter.length === 0) return true;
        return filter.includes(keyParts[idx]);
      });
    });

    // Sort filteredData by all group columns (groupValues)
    filteredData.sort((a, b) => {
      for (let i = 0; i < a.groupValues.length; i++) {
        if (a.groupValues[i] < b.groupValues[i]) return -1;
        if (a.groupValues[i] > b.groupValues[i]) return 1;
      }
      return 0;
    });

    // Prepare headers
    const headers = [
      ...groupings.map(
        (g) =>
          TRADES_GROUP_BY_OPTIONS.find((opt) => opt.value === g.groupBy)
            ?.name || g.groupBy
      ),
      "Trading Result (USD)",
      "Number of Trades",
      "Trading Turnover (USD)",
      "Win Count & Rate (%)",
      "Lose Count & Rate (%)",
      "Draw Count & Rate (%)",
      "Revert Count & Rate (%)",
    ];

    // Prepare CSV content
    csvContent = [
      headers.join(","),
      ...filteredData.map((row) => {
        return [
          ...row.groupValues.map((val) => `"${val}"`),
          `"${
            row.tradingResult < 0
              ? `- $${Math.abs(row.tradingResult)}`
              : `$${row.tradingResult}`
          }"`,
          `"${row.numberOfTrades.toLocaleString()}"`,
          `"$${row.tradingTurnover.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}"`,
          `"${row.winningTradesCount.toLocaleString()} (${row.winRate.toLocaleString(
            undefined,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          )}%)"`,
          `"${row.losingTradesCount.toLocaleString()} (${row.lossRate.toLocaleString(
            undefined,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          )}%)"`,
          `"${row.drawTradesCount.toLocaleString()} (${row.drawRate.toLocaleString(
            undefined,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          )}%)"`,
          `"${row.revertedTradesCount.toLocaleString()} (${row.revertRate.toLocaleString(
            undefined,
            { minimumFractionDigits: 2, maximumFractionDigits: 2 }
          )}%)"`,
        ].join(",");
      }),
    ].join("\n");
  }

  return csvContent;
}
